import { translate } from '../../../helpers/translations/translator'
import {
    LeaveOfAbsenceFormData,
    LeaveOfAbsenceHours,
    LeaveType,
    WeekWithHours,
} from '../../../models'
import { postCommentApiCall } from '../../entities/Comment/CommentService'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import { LeaveOfAbsence } from '../../entities/LeaveOfAbsence/LeaveOfAbsence'
import {
    deleteLeaveOfAbsenceApiCall,
    getLeaveOfAbsencesInPeriodApiCall,
    patchLeaveOfAbsenceApiCall,
    postLeaveOfAbsenceApiCall,
    postLeaveOfAbsenceApproveApiCall,
    postLeaveOfAbsenceDeclineApiCall,
} from '../../entities/LeaveOfAbsence/LeaveOfAbsenceService'
import { updateLeaveOfAbsenceHours } from '../../entities/LeaveOfAbsenceHour/LeaveOfAbsenceHourHelper'
import {
    postLeaveOfAbsenceHoursApiCall,
} from '../../entities/LeaveOfAbsenceHour/LeaveOfAbsenceHourService'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '../../entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '../../entities/NotificationCenter/NotificationCenterService'
import { Period } from '../../entities/Period/Period'
import { ToastType } from '../../types'
import { PzLeaveType } from '../../types/leaveType'
import { TypedDispatch } from '../store'
import { setToast } from '../toasts/toastsReducer'
import { setError, setIsLoading, setLeaveOfAbsences } from './leaveOfAbsencesReducer'
import { getApproveToastMessage, getDeclineToastMessage } from './leaveOfAbsencesHelpers'

export const getLeaveOfAbsencesInPeriod = (period: Period) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await getLeaveOfAbsencesInPeriodApiCall(period, true)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setLeaveOfAbsences(response.data))
    } catch (error) {
        console.error('[getLeaveOfAbsencesInPeriod]', error)
    }

    dispatch(setIsLoading(false))
}

export const addLeaveOfAbsence = (
    userId: string,
    data: LeaveOfAbsenceFormData,
    weeksWithHours: WeekWithHours[],
    leaveType?: LeaveType | PzLeaveType,
    comment?: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.LEAVE_OF_ABSENCE,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await postLeaveOfAbsenceApiCall(data, userId, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        await Promise.all([
            ...[comment && await postCommentApiCall({
                comment,
                relationshipKey: 'leaveOfAbsence',
                relationshipType: 'leaveOfAbsences',
                relationshipId: response.data.id,
                eventLogTrackerData: responseTracker.data,
            })],

            ...weeksWithHours.map(
                week => postLeaveOfAbsenceHoursApiCall(
                    response.data.id,
                    responseTracker.data,
                    week.payrollPeriod ? week.payrollPeriod.id : undefined,
                    week.hours ?? undefined,
                    week.weekNumber,
                    leaveType,
                ),
            ),
        ])

        dispatch(setToast({
            text: translate('pages.absences.addLeaveOfAbsenceSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[addLeaveOfAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const patchLeaveOfAbsence = (
    leaveOfAbsence: LeaveOfAbsence,
    data: LeaveOfAbsenceFormData,
    leaveOfAbsenceHours: LeaveOfAbsenceHours[],
    weeksWithHours: WeekWithHours[],
    leaveType?: LeaveType | PzLeaveType,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.LEAVE_OF_ABSENCE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const responses = await Promise.all([
            patchLeaveOfAbsenceApiCall(leaveOfAbsence.id, data, responseTracker.data),
            ...updateLeaveOfAbsenceHours(
                leaveOfAbsence.id,
                leaveOfAbsenceHours,
                responseTracker.data,
                weeksWithHours,
                leaveType,
            ),
        ])

        const failedResponse = responses.find(response => ! isFetchResultSuccessful(response))

        if (failedResponse && ! isFetchResultSuccessful(failedResponse)) {
            dispatch(setError(failedResponse.error))
            return
        }

        dispatch(setToast({
            text: translate('pages.absences.editLOASuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[patchLeaveOfAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const deleteLeaveOfAbsence = (
    leaveOfAbsence: LeaveOfAbsence,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.LEAVE_OF_ABSENCE,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const response = await deleteLeaveOfAbsenceApiCall(leaveOfAbsence.id, responseTracker.data)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))
        }

        dispatch(setToast({
            text: translate('pages.absences.leaveOfAbsenceDeletedSuccess'),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[deleteLeaveOfAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const declineLeaveOfAbsence = (
    leaveOfAbsence: LeaveOfAbsence,
    comment: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.LEAVE_OF_ABSENCE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        const responses = await Promise.all([
            ...(comment ? [postCommentApiCall({
                comment,
                relationshipKey: 'leaveOfAbsence',
                relationshipType: 'leaveOfAbsences',
                relationshipId: leaveOfAbsence.id,
                eventLogTrackerData: responseTracker.data,
            })] : []),
            postLeaveOfAbsenceDeclineApiCall(leaveOfAbsence.id, responseTracker.data),
        ])

        const failedResponse = responses.find(response => ! isFetchResultSuccessful(response))

        if (failedResponse && ! isFetchResultSuccessful(failedResponse)) {
            dispatch(setError(failedResponse.error))
            return
        }

        dispatch(setToast({
            text: getDeclineToastMessage(!!leaveOfAbsence.changeset),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))
    } catch (error) {
        console.error('[declineLeaveOfAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}

export const approveLeaveOfAbsence = (
    leaveOfAbsence: LeaveOfAbsence,
    data: LeaveOfAbsenceFormData,
    leaveOfAbsenceHours?: LeaveOfAbsenceHours[],
    weeksWithHours?: WeekWithHours[],
    leaveType?: LeaveType | PzLeaveType,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))

    const responseTracker = await startEventLoggerApiCall(
        NotificationCenterCategory.LEAVE_OF_ABSENCE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        dispatch(setError(responseTracker.error))
        return
    }

    try {
        // Update leaveOfAbsence if needed
        if (data) {
            const patchResponse = await patchLeaveOfAbsenceApiCall(leaveOfAbsence.id, data, responseTracker.data)
            if (! isFetchResultSuccessful(patchResponse)) {
                dispatch(setError(patchResponse.error))
                return
            }
        }

        // Approve leaveOfAbsence
        const postResponse = await postLeaveOfAbsenceApproveApiCall(leaveOfAbsence.id, responseTracker.data)
        if (! isFetchResultSuccessful(postResponse)) {
            dispatch(setError(postResponse.error))
            return
        }

        // Update leaveOfAbsenceHours if needed
        if (leaveOfAbsenceHours && weeksWithHours) {
            const hoursPromises = updateLeaveOfAbsenceHours(
                leaveOfAbsence.id,
                leaveOfAbsenceHours,
                responseTracker.data,
                weeksWithHours,
                leaveType,
            )

            const allHoursSuccessful = await hoursPromises.reduce(async (previousPromise, currentPromise) => {
                const shouldContinue = await previousPromise

                if (! shouldContinue) {
                    return false
                }

                const response = await currentPromise

                if (! isFetchResultSuccessful(response)) {
                    dispatch(setError(response.error))
                    return false
                }

                return true
            }, Promise.resolve(true))

            if (! allHoursSuccessful) return
        }

        dispatch(setToast({
            text: getApproveToastMessage(!!leaveOfAbsence.changeset),
            type: ToastType.pass,
            shouldFadeOutAutomatically: true,
        }))

    } catch (error) {
        console.error('[approveLeaveOfAbsence]', error)
    } finally {
        await endEventLoggerApiCall(responseTracker.data)
        dispatch(setIsLoading(false))
    }
}
